'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  useMicroSource,
  type MicroCategorySummary,
  type MicroProgress,
} from '@akademi/site-builder-runtime';
import { MICRO_PROGRESS_UPDATED_EVENT } from './_events';

/**
 * Per-parent card decorations: the learner's progress rows (one
 * {@code fetchMine} read) + the category id → name map (one
 * {@code categories.list} read). Rails/grids call this ONCE and pass
 * {@code progress}/{@code categoryName}/{@code favorite} down to each
 * {@link MicroItemCard} — never per-card fetches (legacy parity: the
 * grid island loaded {@code /progress/me} + categories once).
 *
 * <p>Re-fetches progress on {@link MICRO_PROGRESS_UPDATED_EVENT} so
 * completing an item in the popup or toggling a heart updates every
 * visible card without a page reload.
 */
export interface MicroCardDecorations {
  progressFor(itemId: number | string): MicroProgress | undefined;
  categoryNameFor(categoryId: number | string | null | undefined): string | undefined;
}

export function useMicroCardDecorations(opts?: {
  /** Pass {@code false} when the caller already fetches the category
   *  list itself (e.g. the listing layout's filter pills). */
  categories?: boolean;
}): MicroCardDecorations {
  const micro = useMicroSource();
  const withCategories = opts?.categories !== false;
  const [progressRows, setProgressRows] = useState<ReadonlyMap<string, MicroProgress>>(
    () => new Map(),
  );
  const [categoryNames, setCategoryNames] = useState<ReadonlyMap<string, string>>(() => new Map());

  useEffect(() => {
    const progressNs = micro?.progress;
    const fetchMine = progressNs?.fetchMine ? progressNs.fetchMine.bind(progressNs) : undefined;
    if (!fetchMine) return;
    let cancelled = false;
    const load = () => {
      void fetchMine()
        .then((rows: MicroProgress[]) => {
          if (cancelled) return;
          setProgressRows(new Map(rows.map((r) => [String(r.itemId), r])));
        })
        .catch(() => undefined); // anonymous / offline — no badges
    };
    load();
    window.addEventListener(MICRO_PROGRESS_UPDATED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(MICRO_PROGRESS_UPDATED_EVENT, load);
    };
  }, [micro]);

  useEffect(() => {
    if (!withCategories || !micro) return;
    let cancelled = false;
    void micro.categories
      .list()
      .then((cats: MicroCategorySummary[]) => {
        if (cancelled) return;
        setCategoryNames(new Map(cats.map((c) => [String(c.id), c.name])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [micro, withCategories]);

  const progressFor = useCallback(
    (itemId: number | string) => progressRows.get(String(itemId)),
    [progressRows],
  );
  const categoryNameFor = useCallback(
    (categoryId: number | string | null | undefined) =>
      categoryId == null ? undefined : categoryNames.get(String(categoryId)),
    [categoryNames],
  );

  return { progressFor, categoryNameFor };
}
